export const ARMS_MAX = 20

// The BFF wraps every payload as { success, data }. Read `data`, but fall
// back to a legacy top-level key so a shape change on either side is survivable.
export function unwrap(res, key) {
  const body = (res && res.body) || {}
  if (body.data !== undefined) return body.data
  // A null key means the legacy shape is the whole body (e.g. health has no
  // wrapping key), so fall back to it rather than a dead body[key] lookup.
  return key ? body[key] : body
}

// Validation failures surface as an APIError whose body is { message: 'CODE', ...extra };
// some layers use { error: 'CODE' }. Read either, strip any `:variantKey` suffix.
export function errorCode(err) {
  const body = (err && err.body) || {}
  const raw = body.error || body.message || (body.data && body.data.error) || ""
  return String(raw).split(":")[0]
}

// A route the backend has not shipped yet answers a bare 404; a domain 404 such
// as FLOW_CONFIG_NOT_FOUND means the route exists and must not be read as undeployed.
export function isNotDeployed(err) {
  if (!err || err.status !== 404) return false
  return !/_NOT_FOUND$/.test(errorCode(err))
}

export function friendlyError(err) {
  if (err && err.status === 503) {
    return "This data is temporarily unavailable (manifest not loaded yet). Please try again shortly."
  }
  return "Failed to load this data. Please try again."
}

const VALIDATION_MESSAGES = {
  ARMS_TOTAL_WEIGHT_ZERO: "Arm weights must sum to more than zero.",
  ARMS_MULTIPLE_CONTROL: "Only one arm may be the control.",
  ARM_DUPLICATE_VARIANT: "Each variant and slide list pair may appear only once.",
  ARM_VARIANT_NOT_IN_BUILD: "An arm points at a variant that is not in the current build.",
  ARM_WEIGHT_NEGATIVE: "Arm weights cannot be negative.",
  ARM_WEIGHT_NOT_FINITE: "Arm weights must be numbers.",
  MATCH_FIELDS_IMMUTABLE: "The landing path cannot be changed on an existing config; create a new config instead.",
  FLOW_CONFIG_VERSION_CONFLICT: "This config changed since you opened it. Reloading the latest; please review and try again.",
}

export function friendlyValidation(err) {
  const code = errorCode(err)
  const detail = (err && err.body) || {}
  if (code === "MATCH_VALUE_NOT_WHITELISTED") {
    const allowed = Array.isArray(detail.allowed) ? " Allowed paths: " + detail.allowed.join(", ") : ""
    return "That landing path is not whitelisted. Adding a new path is a code change." + allowed
  }
  if (VALIDATION_MESSAGES[code]) return VALIDATION_MESSAGES[code]
  if (err && err.status === 409) return VALIDATION_MESSAGES.FLOW_CONFIG_VERSION_CONFLICT
  return code ? "Save failed: " + code : "Save failed. Please try again."
}

export function armsSummary(arms, variantKeys) {
  const messages = []
  let valid = true
  if (!arms.length) {
    return { valid: false, messages: ["Add at least one arm."] }
  }
  const variantSet = new Set(variantKeys || [])
  const seen = new Set()
  let total = 0
  let controlCount = 0
  let duplicatePair = false
  let offBuild = false
  let badWeight = false
  let missingVariant = false
  arms.forEach((arm) => {
    if (!arm.variantKey) missingVariant = true
    if (arm.variantKey) {
      const pairKey = arm.variantKey + "|" + (arm.segmentKey || "")
      if (seen.has(pairKey)) duplicatePair = true
      seen.add(pairKey)
      if (variantSet.size && !variantSet.has(arm.variantKey)) offBuild = true
    }
    if (!Number.isFinite(arm.weight) || arm.weight < 0) badWeight = true
    else total += arm.weight
    if (arm.isControl) controlCount += 1
  })
  if (missingVariant) { messages.push("Every arm needs a variant."); valid = false }
  if (badWeight) { messages.push("Weights must be numbers of zero or more."); valid = false }
  if (total <= 0) { messages.push("Weights must sum to more than zero."); valid = false }
  if (controlCount > 1) { messages.push("At most one arm may be the control."); valid = false }
  if (duplicatePair) { messages.push("Each variant and slide list pair may appear only once."); valid = false }
  if (offBuild) { messages.push("An arm points at a variant not in the current build."); valid = false }
  if (valid) messages.push("Valid; weights sum to " + total + ".")
  return { valid, messages }
}

export function serialiseArms(arms) {
  return arms.map((arm) => {
    const out = { variantKey: arm.variantKey, weight: Number(arm.weight), isControl: Boolean(arm.isControl) }
    if (arm.segmentKey) out.segmentKey = arm.segmentKey
    return out
  })
}

export function editableArms(arms) {
  return (arms || []).map((arm) => ({
    variantKey: arm.variantKey,
    segmentKey: arm.segmentKey || null,
    weight: arm.weight,
    isControl: Boolean(arm.isControl),
  }))
}

export function addArm(arms) {
  if (arms.length >= ARMS_MAX) return
  arms.push({ variantKey: "", segmentKey: null, weight: 0, isControl: false })
}

export function setControl(arms, index) {
  arms.forEach((arm, i) => {
    arm.isControl = i === index
  })
}
